import { useCambiosSinGuardar, useSubpantalla } from '../ui/navegacionReact';
import { navegacion } from '../ui/navegacion';
import { useCallback, useEffect, useState } from 'react';
import {
  BookOpen, Loader2, Search, Plus, ChevronLeft, Pencil, Archive, Save,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import Ayuda from '../ui/Ayuda';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';

/* =====================================================================
   Base de conocimiento · DIRECTRIZ-TICKETS F7
   =====================================================================
   Las soluciones que el equipo ya encontró, guardadas para reusar. Cualquiera
   de la empresa puede buscar y leer; quien atiende crea, edita y retira. El
   backend acota todo a la empresa (RLS) y comprueba el permiso.

   Móvil de verdad: objetivos táctiles grandes, buscador arriba, una sola
   columna. Estados vacío/cargando/error útiles en cada vista.
   ===================================================================== */

const CATEGORIAS = [
  { clave: 'TI', nombre: 'TI' },
  { clave: 'MANTENIMIENTO', nombre: 'Mantenimiento' },
  { clave: 'ALMACEN', nombre: 'Almacén' },
  { clave: 'SEGURIDAD', nombre: 'Seguridad' },
  { clave: 'TEGO', nombre: 'Problema con TEGO' },
];
const NOMBRE_CATEGORIA = Object.fromEntries(CATEGORIAS.map((c) => [c.clave, c.nombre]));

export default function BaseConocimiento() {
  const { puede } = useAuth();
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [abierto, setAbierto] = useState(null);   // id del artículo abierto
  useSubpantalla('articulo', !!abierto, () => setAbierto(null), { valor: abierto, abrir: setAbierto });
  const [creando, setCreando] = useState(false);
  useSubpantalla('creando', creando, () => setCreando(false));
  // ¿Puede quien mira crear/editar/retirar? El backend manda; esto solo
  // evita enseñar botones que terminarían en un «no puedes» (usuario cero).
  const cura = puede('tickets.atender') || puede('tickets.administrar');

  const recargar = useCallback(() => {
    const ruta = q.trim() ? `/tickets/articulos?q=${encodeURIComponent(q.trim())}` : '/tickets/articulos';
    api.get(ruta)
      .then((l) => { setLista(l); setError(''); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar la base de conocimiento.'));
  }, [q]);
  // Busca al escribir (en vivo), con una pausa corta para no llamar por letra.
  useEffect(() => {
    const t = setTimeout(recargar, 250);
    return () => clearTimeout(t);
  }, [recargar]);

  if (creando) {
    return <Editor sub="creando" onVolver={() => { setCreando(false); recargar(); }} />;
  }
  if (abierto) {
    return <Detalle id={abierto} cura={cura}
                    onVolver={() => { setAbierto(null); recargar(); }} />;
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
          <BookOpen className="h-5 w-5" aria-hidden="true" /> Base de conocimiento
        </h1>
        <p className="text-gray-600">Soluciones que tu equipo ya encontró, listas para reusar.</p>
        <Ayuda id="conocimiento.pantalla" />
      </header>

      <div>
        <label htmlFor="kb-buscar" className="mb-1 block text-sm font-medium text-gray-700">
          Buscar una solución
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            id="kb-buscar" type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Ej.: la impresora no imprime"
            className="min-h-[48px] w-full rounded-xl border border-gray-300 pl-10 pr-3 text-base
                       focus:border-[var(--color-marca)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
          />
        </div>
        <Ayuda id="conocimiento.buscar" />
      </div>

      {cura && (
        <button
          type="button" onClick={() => setCreando(true)}
          className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl
                     bg-[var(--color-marca)] px-4 font-semibold text-white hover:opacity-95"
        >
          <Plus className="h-5 w-5" aria-hidden="true" /> Escribir un artículo
        </button>
      )}

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      {lista === null && !error && (
        <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando la base de conocimiento…
        </p>
      )}

      {lista !== null && lista.length === 0 && (
        <EstadoVacio
          icono={BookOpen}
          titulo={q.trim() ? 'No hay artículos que se parezcan a eso' : 'Todavía no hay artículos'}
          descripcion={q.trim()
            ? 'Prueba con otras palabras, o escribe un artículo nuevo con la solución.'
            : 'Cuando resuelvas un ticket, guarda la solución aquí para que sirva la próxima vez.'}
        />
      )}

      {lista !== null && lista.length > 0 && (
        <ul className="flex flex-col gap-2">
          {lista.map((a) => (
            <li key={a.id}>
              <button
                type="button" onClick={() => setAbierto(a.id)}
                className="flex w-full items-center justify-between gap-3 rounded-xl border
                           border-gray-200 p-3 text-left hover:bg-gray-50"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-900">{a.titulo}</span>
                  <span className="block text-xs text-gray-500">
                    {NOMBRE_CATEGORIA[a.categoria] || a.categoria} · <TiempoRelativo valor={a.actualizado_en} />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// El detalle de un artículo: leerlo, editarlo o retirarlo
// ---------------------------------------------------------------------
function Detalle({ id, cura, onVolver }) {
  const [a, setA] = useState(null);
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(false);
  useSubpantalla('editando', editando, () => setEditando(false));
  const [ocupado, setOcupado] = useState(false);

  function recargar() {
    api.get(`/tickets/articulos/${id}`)
      .then((d) => { setA(d); setError(''); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo abrir el artículo.'));
  }
  useEffect(recargar, [id]);

  async function retirar() {
    if (ocupado) return;
    setOcupado(true);
    try { await api.post(`/tickets/articulos/${id}/baja`, {}); recargar(); }
    catch (e) { setError(e instanceof ErrorApi ? e.message : 'No se pudo retirar el artículo.'); }
    finally { setOcupado(false); }
  }

  if (editando && a) {
    return <Editor inicial={a} sub="editando"
                   onVolver={() => { setEditando(false); recargar(); }} />;
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <button type="button" onClick={onVolver}
              className="flex items-center gap-1 self-start text-sm text-gray-600 hover:text-gray-900">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver a la base de conocimiento
      </button>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {!a && !error && (
        <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Abriendo el artículo…
        </p>
      )}

      {a && (
        <>
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">{a.titulo}</h1>
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                {NOMBRE_CATEGORIA[a.categoria] || a.categoria}
              </span>
              {!a.activo && (
                <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  Retirado
                </span>
              )}
            </div>
          </header>

          {a.problema && (
            <section>
              <h2 className="mb-1 text-sm font-semibold text-gray-700">El problema</h2>
              <p className="whitespace-pre-wrap text-sm text-gray-800">{a.problema}</p>
            </section>
          )}
          <section>
            <h2 className="mb-1 text-sm font-semibold text-gray-700">La solución</h2>
            <p className="whitespace-pre-wrap text-sm text-gray-800">{a.solucion}</p>
          </section>

          {cura && (
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button" onClick={() => setEditando(true)}
                className="flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-300 px-4 font-medium text-gray-700 hover:bg-gray-50"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" /> Editar
              </button>
              {a.activo && (
                <div>
                  <button
                    type="button" onClick={retirar} disabled={ocupado}
                    className="flex min-h-[44px] items-center gap-2 rounded-xl border border-amber-300 px-4 font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                  >
                    <Archive className="h-4 w-4" aria-hidden="true" /> Retirar
                  </button>
                  <Ayuda id="conocimiento.baja" />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// El editor: escribir o corregir un artículo
// ---------------------------------------------------------------------
function Editor({ inicial, onVolver, sub }) {
  const esEdicion = !!inicial;
  const [titulo, setTitulo] = useState(inicial?.titulo || '');
  const [problema, setProblema] = useState(inicial?.problema || '');
  const [solucion, setSolucion] = useState(inicial?.solucion || '');
  const [categoria, setCategoria] = useState(inicial?.categoria || 'TI');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');

  const listo = titulo.trim() && solucion.trim();
  // Artículo a medio escribir (TC-DR-AT): atrás y «Volver» preguntan antes
  // de cerrarlo. Tras guardar se cierra solo, sin preguntar.
  useCambiosSinGuardar(() => !ocupado && (
    titulo !== (inicial?.titulo || '') || problema !== (inicial?.problema || '')
    || solucion !== (inicial?.solucion || '') || categoria !== (inicial?.categoria || 'TI')), { sub });

  async function guardar() {
    if (!listo || ocupado) return;
    setOcupado(true);
    setError('');
    try {
      const cuerpo = { titulo: titulo.trim(), problema: problema.trim() || null, solucion: solucion.trim(), categoria };
      if (esEdicion) await api.put(`/tickets/articulos/${inicial.id}`, cuerpo);
      else await api.post('/tickets/articulos', cuerpo);
      onVolver();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo guardar el artículo.');
    } finally { setOcupado(false); }
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <button type="button" onClick={() => navegacion.conGuardia(onVolver)}
              className="flex items-center gap-1 self-start text-sm text-gray-600 hover:text-gray-900">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver
      </button>
      <h1 className="text-lg font-semibold text-gray-900">
        {esEdicion ? 'Editar artículo' : 'Escribir un artículo'}
      </h1>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      <div>
        <label htmlFor="kb-titulo" className="mb-1 block text-sm font-medium text-gray-700">
          Título <span className="text-red-600">*</span>
        </label>
        <input
          id="kb-titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ej.: La impresora no imprime"
          className="min-h-[48px] w-full rounded-xl border border-gray-300 px-3 text-base
                     focus:border-[var(--color-marca)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
        />
      </div>

      <div>
        <label htmlFor="kb-categoria" className="mb-1 block text-sm font-medium text-gray-700">Categoría</label>
        <select
          id="kb-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)}
          className="min-h-[48px] w-full rounded-xl border border-gray-300 px-3 text-base
                     focus:border-[var(--color-marca)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
        >
          {CATEGORIAS.map((c) => <option key={c.clave} value={c.clave}>{c.nombre}</option>)}
        </select>
      </div>

      <div>
        <label htmlFor="kb-problema" className="mb-1 block text-sm font-medium text-gray-700">El problema</label>
        <textarea
          id="kb-problema" value={problema} onChange={(e) => setProblema(e.target.value)} rows={2}
          placeholder="Cómo se ve el problema, con las palabras de quien lo reporta."
          className="w-full rounded-xl border border-gray-300 p-3 text-base
                     focus:border-[var(--color-marca)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
        />
      </div>

      <div>
        <label htmlFor="kb-solucion" className="mb-1 block text-sm font-medium text-gray-700">
          Solución <span className="text-red-600">*</span>
        </label>
        <textarea
          id="kb-solucion" value={solucion} onChange={(e) => setSolucion(e.target.value)} rows={5}
          placeholder="Cómo se resuelve, paso a paso."
          className="w-full rounded-xl border border-gray-300 p-3 text-base
                     focus:border-[var(--color-marca)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
        />
        <Ayuda id="conocimiento.solucion" />
      </div>

      <button
        type="button" onClick={guardar} disabled={!listo || ocupado}
        title={!listo ? 'Escribe al menos el título y la solución' : undefined}
        className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl
                   bg-[var(--color-marca)] px-4 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {ocupado ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Save className="h-5 w-5" aria-hidden="true" />}
        {esEdicion ? 'Guardar cambios' : 'Guardar artículo'}
      </button>
      {!listo && (
        <p className="text-xs text-gray-500">Faltan el título y la solución para poder guardar.</p>
      )}
    </div>
  );
}
